import RoomFilterSuggestionItem from './RoomFilterSuggestionItem';
import { RoomFilterType } from './RoomFilterType';
import RoomFilterWellItem from './RoomFilterWellItem';
import RoomFilterWellItemViewState from './RoomFilterWellItemViewState';
import { RoomList } from './RoomSelector.types';
import getStore from '../../store/store';
import { observer } from 'mobx-react';
import { Icon } from '@fluentui/react/lib/Icon';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { themeFromColorScheme } from 'owa-color-utils/lib/colorUtils';
import { ControlIcons } from 'owa-control-icons';
import loc from 'owa-localize';
import { anyRoomListName } from 'owa-locstrings/lib/strings/anyroomlistname.locstring.json';
import { getRoomsFromPlacesApi } from '../../utils/getRooms';
import * as React from 'react';
import {
    roomPanelFilterWellNoResultsFound,
    roomPanelFilterWellNoRoomLists,
    roomPanelFilterWellLoadingMoreRoomLists,
    roomPanelFilterWellShowAllRooms,
    roomPanelFilterWellPlaceholderLabel,
} from './RoomFilterWell.locstring.json';
import { IPeopleFloatingPickerProps, SuggestionsStore } from '@fluentui/react/lib/FloatingPicker';
import {
    ISelectedPeopleItemProps,
    ISelectedPeopleProps,
} from '@fluentui/react/lib/SelectedItemsList';
import {
    ReadWriteCommonWell,
    ReadWriteCommonWellProps,
    ReadWriteCommonWellState,
} from 'owa-readwrite-common-well/lib/components/ReadWriteCommonWell';
import {
    increaseTotalNumSelectedCities,
    increaseTotalNumSelectedRoomLists,
    queryStringChanged,
} from '../../mutators/roomPanelMetricsMutators';

export interface RoomFilterWellProps
    extends ReadWriteCommonWellProps<
        RoomFilterWellItemViewState,
        RoomFilterWellItemViewState,
        RoomFilterWellItemViewState
    > {
    colorScheme: ColorScheme;
    onRoomFilterWellItemsChanged: (items: RoomFilterWellItemViewState[]) => void;
}

export interface RoomFilterWellState extends ReadWriteCommonWellState<RoomFilterWellItemViewState> {
    numberOfSuggestions: number;
}

const styles = require('./RoomFilterWell.scss');
let classNames = require('classnames/bind').bind(styles);
const FIRST_LOAD_SUGGESTIONS: number = 10;

@observer
export default class RoomFilterWell extends ReadWriteCommonWell<
    RoomFilterWellProps,
    RoomFilterWellItemViewState,
    RoomFilterWellItemViewState,
    RoomFilterWellItemViewState,
    RoomFilterWellState
> {
    private roomListFilterItems: RoomFilterWellItemViewState[] = [];

    constructor(props: RoomFilterWellProps) {
        super(props);

        this.state = {
            items: [],
            numberOfSuggestions: 0,
        };
    }

    componentDidMount() {
        super.componentDidMount();

        /**
         * Prevents browsers from offering their own autocomplete suggestions for addresses
         * and credentials in the user's local cache
         */
        this.picker.inputElement.autocomplete = 'disabled';
        this.picker.focus();
    }

    public render(): JSX.Element {
        this.roomListFilterItems = this.convertRoomListsToFilterItems(
            getStore().roomListViewState.roomLists
        );

        return (
            <div className={styles.container}>
                {this.renderSearchIcon()}
                {super.render()}
            </div>
        );
    }

    protected getFindControlProps = (): Partial<IPeopleFloatingPickerProps> => {
        return {
            suggestionsStore: new SuggestionsStore<RoomFilterWellItemViewState>(),
            onRenderSuggestionsItem: this.onRenderRoomFilterSuggestionItem,
            onResolveSuggestions: this.onResolveRoomFilterSuggestions,
            onZeroQuerySuggestion: this.onRoomFiltersZeroQuery,
            calloutWidth: 300,
            resolveDelay: 300,
            onInputChanged: this.onQueryStringChanged,
            pickerSuggestionsProps: {
                footerItemsProps: [
                    {
                        renderItem: () => {
                            return (
                                <div className={styles.noResults}>
                                    {loc(roomPanelFilterWellNoRoomLists)}
                                </div>
                            );
                        },
                        shouldShow: () => {
                            return (
                                this.state.numberOfSuggestions == 0 &&
                                this.picker.floatingPicker.current.inputText.length == 0
                            );
                        },
                    },
                    {
                        renderItem: () => {
                            return (
                                <div className={styles.noResults}>
                                    {loc(roomPanelFilterWellNoResultsFound)}
                                </div>
                            );
                        },
                        shouldShow: () => {
                            return (
                                this.state.numberOfSuggestions == 0 &&
                                this.picker.floatingPicker.current.inputText.length != 0
                            );
                        },
                    },
                    {
                        renderItem: () => {
                            return (
                                <div className={styles.noResults}>
                                    <span>{loc(roomPanelFilterWellLoadingMoreRoomLists)}</span>
                                </div>
                            );
                        },
                        shouldShow: () => {
                            // Show the footer when not all the room lists are loaded
                            return (
                                this.state.numberOfSuggestions < this.roomListFilterItems.length &&
                                this.picker.floatingPicker.current.inputText.length == 0
                            );
                        },
                    },
                    {
                        renderItem: () => {
                            const theme = {
                                color: this.props.colorScheme.calSecondary,
                            };

                            return (
                                <div className={styles.allRoomsContainer}>
                                    <Icon
                                        iconName={ControlIcons.CityNext}
                                        className={styles.allRoomsIcon}
                                        style={theme}
                                    />
                                    <span className={styles.allRooms} style={theme}>
                                        {loc(roomPanelFilterWellShowAllRooms)}
                                    </span>
                                </div>
                            );
                        },
                        shouldShow: () => {
                            return this.roomListFilterItems.length == 0;
                        },
                        onExecute: () => {
                            this.onItemsAdded([
                                {
                                    displayText: loc(anyRoomListName),
                                    filterType: RoomFilterType.RoomList,
                                    isContextMenuOpen: false,
                                    isSelected: false,
                                    isValid: true,
                                    queryableId: loc(anyRoomListName),
                                },
                            ]);

                            this.picker.clearInput();
                            this.picker.floatingPicker.current.hidePicker();
                        },
                    },
                ],
                shouldSelectFirstItem: () => {
                    return false;
                },
            },
        };
    };

    protected extendedPickerProps(): any {
        return {
            inputProps: {
                placeholder:
                    this.state.items && this.state.items.length > 0
                        ? ''
                        : loc(roomPanelFilterWellPlaceholderLabel),
                'aria-label': loc(roomPanelFilterWellPlaceholderLabel),
            },
            itemLimit: 5,
            className: classNames(styles.input, {
                [styles.inputExtraPadding]: this.state.items.length == 0,
            }),
        };
    }

    protected onItemsAdded = (itemsToAdd: RoomFilterWellItemViewState[]) => {
        const processedItemsToAdd = this.processItemsToAdd(itemsToAdd);
        let newItems: RoomFilterWellItemViewState[] = [];

        if (processedItemsToAdd) {
            const itemsToAddContainAllRooms: boolean = processedItemsToAdd.some(
                x => x.queryableId == loc(anyRoomListName)
            );
            const currentItemsContainAllRooms: boolean = this.state.items.some(
                x => x.queryableId == loc(anyRoomListName)
            );

            if (itemsToAddContainAllRooms || currentItemsContainAllRooms) {
                // All Rooms is mutually exclusive with any other filters
                newItems = processedItemsToAdd;
            } else {
                newItems = this.state.items.concat(processedItemsToAdd);
            }
        }

        this.setState({ items: newItems }, this.onItemsChanged);
    };

    protected getExtendedSelectedItemsListProps = (): ISelectedPeopleProps => {
        return null;
    };

    protected onItemSelected = (item: RoomFilterWellItemViewState): RoomFilterWellItemViewState => {
        if (!this.containsSelectedItem(item)) {
            item.filterType === RoomFilterType.City
                ? increaseTotalNumSelectedCities()
                : increaseTotalNumSelectedRoomLists();
        }
        return this.containsSelectedItem(item) ? null : item;
    };

    protected renderSelectedItem = (props: ISelectedPeopleItemProps): JSX.Element => {
        let viewState = props.item as RoomFilterWellItemViewState;

        return (
            <RoomFilterWellItem
                key={`${viewState.queryableId}_${props.index}`}
                index={props.index}
                viewState={viewState}
                removeOperation={this.removeRoomFilter}
                useContextMenu={false}
                isSelected={props.selected}
                colorScheme={this.props.colorScheme}
                theme={themeFromColorScheme(this.props.colorScheme)}
                maxItemWidth={230}
            />
        );
    };

    protected convertSuggestionItemToWellItem = (
        suggestionItem: RoomFilterWellItemViewState
    ): RoomFilterWellItemViewState => {
        return suggestionItem;
    };

    protected convertInitialItemsToWellItems = (
        items: RoomFilterWellItemViewState[]
    ): RoomFilterWellItemViewState[] => {
        return items;
    };

    protected onItemsChanged = (): void => {
        this.props.onRoomFilterWellItemsChanged(this.state.items);
    };

    protected onSelectedItemsChanged = (items: RoomFilterWellItemViewState[]): void => {
        return;
    };

    protected onPaste = async (pastedText: string): Promise<RoomFilterWellItemViewState[]> => {
        return null;
    };

    protected async parsePastedTextForItems(
        pastedText: string
    ): Promise<RoomFilterWellItemViewState[]> {
        return null;
    }

    protected onKeyDown = async (ev: React.KeyboardEvent<unknown>) => {
        return null;
    };

    /**
     * Used for drag and drop. Since drag and drop does not happen in room filter well,
     * we return -1 as a generic value
     * @param item - the item for which to get the index
     */
    protected getItemIndex = (item: RoomFilterWellItemViewState) => {
        return -1;
    };

    private renderSearchIcon = () => {
        return (
            this.state.items.length == 0 && (
                <div
                    className={styles.searchIconContainer}
                    style={{ color: this.props.colorScheme.calPrimary }}>
                    <Icon iconName={ControlIcons.Search} className={styles.searchIcon} />
                </div>
            )
        );
    };

    private containsSelectedItem = (item: RoomFilterWellItemViewState): boolean => {
        if (this.state.items) {
            return this.state.items.some(x => x.queryableId == item.queryableId);
        }

        return false;
    };

    private removeRoomFilter = (roomFilter: RoomFilterWellItemViewState) => {
        this.picker.selectedItemsList.current.removeItems([roomFilter]);
    };

    private onRenderRoomFilterSuggestionItem = (
        props: RoomFilterWellItemViewState,
        itemProps: any
    ): JSX.Element => {
        return <RoomFilterSuggestionItem colorScheme={this.props.colorScheme} viewState={props} />;
    };

    private onResolveRoomFilterSuggestions = (
        filter: string,
        selectedItems: RoomFilterWellItemViewState[]
    ): Promise<RoomFilterWellItemViewState[]> | null => {
        if (filter) {
            return Promise.resolve(this.getFilters(filter, this.roomListFilterItems));
        }

        return null;
    };

    private onRoomFiltersZeroQuery = (
        selectedItems?: RoomFilterWellItemViewState[]
    ): RoomFilterWellItemViewState[] | null => {
        if (this.roomListFilterItems) {
            const numberOfItemsToReturn: number =
                this.roomListFilterItems.length > FIRST_LOAD_SUGGESTIONS
                    ? FIRST_LOAD_SUGGESTIONS
                    : this.roomListFilterItems.length;
            const zeroQuerySuggestionItems: RoomFilterWellItemViewState[] = this.roomListFilterItems.slice(
                0,
                numberOfItemsToReturn
            );
            this.setState({ numberOfSuggestions: zeroQuerySuggestionItems.length });

            if (this.roomListFilterItems.length > FIRST_LOAD_SUGGESTIONS) {
                setTimeout(() => {
                    this.picker &&
                        this.picker.floatingPicker.current.updateSuggestions(
                            this.roomListFilterItems,
                            true
                        );
                    this.setState({
                        numberOfSuggestions: this.roomListFilterItems.length,
                    });
                }, 0);
            }

            return zeroQuerySuggestionItems;
        }

        return null;
    };

    private convertRoomListsToFilterItems = (
        roomLists: RoomList[]
    ): RoomFilterWellItemViewState[] => {
        if (roomLists) {
            return roomLists.map(x => {
                return {
                    displayText: x.name,
                    filterType: RoomFilterType.RoomList,
                    isContextMenuOpen: false,
                    isSelected: false,
                    isValid: true,
                    queryableId: x.email,
                };
            });
        }

        return [];
    };

    private onQueryStringChanged(queryString: string) {
        // log if the query string in room filter well has ever changed
        if (!getStore().roomPanelMetricsState.queryStringChanged) {
            queryStringChanged();
        }
    }

    async getFilters(filter: string, roomLists: RoomFilterWellItemViewState[]) {
        const cities: RoomFilterWellItemViewState[] = await this.getCities(filter);
        const filteredRoomLists: RoomFilterWellItemViewState[] = roomLists.filter(
            x => x.displayText.toLowerCase().indexOf(filter.toLocaleLowerCase()) != -1
        );
        let results: RoomFilterWellItemViewState[] = [];

        if (cities) {
            cities.forEach(x => {
                if (!results.some(y => y.displayText == x.displayText)) {
                    results.push(x);
                }
            });
        }

        if (filteredRoomLists) {
            filteredRoomLists.forEach(x =>
                results.push({
                    ...x,
                    queryString: filter,
                })
            );
        }

        this.setState({ numberOfSuggestions: results.length });
        return results;
    }

    async getCities(filter: string) {
        const getRoomsResult = await getRoomsFromPlacesApi({ cities: [filter] });

        return getRoomsResult?.map(x => ({
            displayText: x.city,
            filterType: RoomFilterType.City,
            isContextMenuOpen: false,
            isSelected: false,
            isValid: true,
            queryableId: x.city,
            queryString: filter,
        }));
    }
}
